import { z } from 'zod';
import i18n from '../i18n';
import { Section } from './section';
import { ParentCompany, TEMPLATE_STATUS, TemplateOwner, TemplateStatus } from './template';

export type TemplateDetail = {
  id: number;
  template_uuid: string;
  name: string;
  type: string;
  prefix: string;
  company_id: number;
  created_time: Date;
  modified_time?: Date;
  sections: Section[];
  status: TemplateStatus;
  version: number | string;
  major_version?: number;
  minor_version?: number;
  document_effective_on_approval: boolean;
  document_review_period: number;
  document_review_due_in_days: number;
  document_approval_due_in_days: number;
  document_text_sections_visible: boolean;
  training_available: boolean;
  training_required: boolean;
  sectioned_content: boolean;
  file_download_user_groups?: MedtechUserGroup[];
  owner?: TemplateOwner;
  owner_full_name?: string;
  owner_id?: number;
  latest_template_id?: number;
  parent?: ParentCompany | null;
  tag_ids?: number[];
  shared_company_ids?: number[];
};

export type TemplateVersions = {
  versions: TemplateDetail[];
  latest_version_id: number;
};

export type MedtechUserGroup = 'quality' | 'normal' | 'read_only';

export const UserGroupInputSchema = z.enum(['owner', 'quality', 'normal', 'read_only']);

export const UpdateTemplatePropertiesSchema = z.object({
  name: z
    .string({
      required_error: i18n.t('common.validation.titleRequired'),
    })
    .trim()
    .min(1, { message: i18n.t('common.validation.titleRequired') }),
  prefix: z
    .string({
      required_error: i18n.t('common.validation.prefixRequired'),
    })
    .trim()
    .min(1, { message: i18n.t('common.validation.prefixRequired') })
    .refine((v) => v.match(/^[0-9a-zA-z]+$/), i18n.t('common.validation.prefixFormat')),
  training_available: z.boolean(),
  training_required: z.boolean(),
  document_review_period: z.number().nullable(),
  document_review_due_in_days: z
    .number()
    .max(180, { message: i18n.t('template.properties.validation.reviewLimitMax') })
    .min(0, { message: i18n.t('template.properties.validation.reviewLimitMin') })
    .nullable(),
  document_approval_due_in_days: z
    .number()
    .max(180, { message: i18n.t('template.properties.validation.approvalLimitMax') })
    .min(0, { message: i18n.t('template.properties.validation.approvalLimitMin') })
    .nullable(),
  file_download_user_groups: UserGroupInputSchema,
  document_effective_on_approval: z.boolean(),
  document_text_sections_visible: z.boolean(),
  tag_ids: z.array(z.number()).default([]),
});

export type UpdateTemplateProperties = z.infer<typeof UpdateTemplatePropertiesSchema>;

export const SectionSchema = z.object({
  title: z
    .string({
      required_error: i18n.t('common.validation.titleRequired'),
    })
    .trim()
    .min(1, { message: i18n.t('common.validation.titleRequired') })
    .max(200, { message: i18n.t('common.validation.maxCharacters') }),
  content: z.string().default(''),
  position: z.number().optional(),
  type: z.string().optional(),
  id: z.number().optional(),
  template_id: z.number().optional(),
  prev_version_section_id: z.number().optional().nullable(),
});

export const UpdateSectionSchema = z.object({
  sections: z.array(SectionSchema),
});

export type UpdateSection = z.infer<typeof UpdateSectionSchema>;

export type ValidUpgradableTemplateStatus = Extract<
  TemplateStatus,
  typeof TEMPLATE_STATUS.effective | typeof TEMPLATE_STATUS.archived
>;
